import type { SessionParticipant } from '~/gql/sessions/sessions'
import { useMutation } from '@apollo/client/react'
import { Undo2, UserMinus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { GQL_CANCEL_DROPOUT, GQL_DECLARE_DROPOUT } from '~/gql/sessions/session-dropouts'
import DropoutDialog from './DropoutDialog'

type IProps = {
  sessionId: string
  participant: SessionParticipant
  /** Relit les résultats après la déclaration ou l'annulation. */
  onChanged: () => void
}

/**
 * L'action d'abandon d'une ligne de participant (#324) : déclarer, avec son
 * motif, ou annuler pour un apprenant qui reprend sa formation.
 */
function DropoutAction({ sessionId, participant, onChanged }: IProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [declare, { loading: declaring }] = useMutation(GQL_DECLARE_DROPOUT)
  const [cancel, { loading: cancelling }] = useMutation(GQL_CANCEL_DROPOUT)
  const name = [participant.firstName, participant.lastName].filter(Boolean).join(' ') || participant.email || ''
  const ref = { sessionId, userId: participant.userId }

  const run = async (action: () => Promise<unknown>, done: string) => {
    try {
      await action()
      toast.success(t(done))
      setOpen(false)
      onChanged()
    } catch {
      toast.error(t('sessionDetail.dropout.failed'))
    }
  }

  if (participant.droppedAt) {
    return (
      <Button
        variant="ghost"
        size="sm"
        disabled={cancelling}
        onClick={() => void run(() => cancel({ variables: { input: ref } }), 'sessionDetail.dropout.cancelled')}
        aria-label={t('common.actionFor', { action: t('sessionDetail.dropout.cancel'), name })}
      >
        <Undo2 className="mr-2 size-4" />
        {t('sessionDetail.dropout.cancel')}
      </Button>
    )
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        aria-label={t('common.actionFor', { action: t('sessionDetail.dropout.declare'), name })}
      >
        <UserMinus className="mr-2 size-4" />
        {t('sessionDetail.dropout.declare')}
      </Button>
      {open && (
        <DropoutDialog
          open={open}
          learnerName={name}
          busy={declaring}
          onCancel={() => setOpen(false)}
          onConfirm={({ reason, note }) => void run(
            () => declare({ variables: { input: { ...ref, reason, note: note || undefined } } }),
            'sessionDetail.dropout.declared',
          )}
        />
      )}
    </>
  )
}

export default DropoutAction
